'use client';

import { FC, useState } from 'react';

import { DisplayOrder, OrderBaseUrls, OrderLineOpenedFrom } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Expand } from '@ravago/shared/radiance/components/expand/components/Expand/Expand';

import { OrderExpandHeader } from '../../content/order-details/components/order-expand-header';
import { OrderLinesTable } from '../order-lines-table/order-lines-table';

interface Props {
   locale: string;
   isConsumer: boolean;
   acceptWebp?: boolean;
   openOrderLines: DisplayOrder[];
   closedOrderLines: DisplayOrder[];
   openedFrom: OrderLineOpenedFrom;
   orderBaseUrls: OrderBaseUrls;
   isUSBusinessContext: boolean;
   showInvoiceDocuments: boolean;
   requestCoa: (orderNumber: string, producerLotNr: string) => Promise<boolean>;
   requestDeliveryNote: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   requestBol: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   downloadDocument: (downloadEndpoint: string, correlationId: string) => Promise<string | undefined>;
}

export const OrderDetailsOrderLines: FC<Props> = ({
   locale,
   isConsumer,
   acceptWebp,
   openOrderLines,
   closedOrderLines,
   orderBaseUrls,
   openedFrom,
   isUSBusinessContext,
   showInvoiceDocuments,
   requestCoa,
   requestDeliveryNote,
   requestBol,
   downloadDocument
}) => {
   const [isOpenOrderLineExpanded, setIsOpenOrderLineExpanded] = useState(true);
   const [isCloseOrderLineExpanded, setIsCloseOrderLineExpanded] = useState(true);
   const [openOrderLinesPage, setOpenOrderLinesPage] = useState(1);
   const [closedOrderLinesPage, setClosedOrderLinesPage] = useState(1);

   return (
      <Box direction="column" gap="lg" width="100%">
         {openOrderLines.length > 0 && (
            <Expand
               expanded={isOpenOrderLineExpanded}
               header={<OrderExpandHeader title="Open" icon="clock" isExpanded={isOpenOrderLineExpanded} amountOfOrders={openOrderLines.length} />}
               onExpandToggle={() => setIsOpenOrderLineExpanded(!isOpenOrderLineExpanded)}
            >
               <OrderLinesTable
                  acceptWebp={acceptWebp}
                  orderLines={openOrderLines}
                  locale={locale}
                  isConsumer={isConsumer}
                  orderBaseUrls={orderBaseUrls}
                  openedFrom={openedFrom}
                  showInvoiceDocuments={showInvoiceDocuments}
                  page={openOrderLinesPage}
                  onPageChange={setOpenOrderLinesPage}
                  requestCoa={requestCoa}
                  requestBol={requestBol}
                  requestDeliveryNote={requestDeliveryNote}
                  downloadDocument={downloadDocument}
                  isUSBusinessContext={isUSBusinessContext}
               />
            </Expand>
         )}

         {closedOrderLines.length > 0 && (
            <Expand
               expanded={isCloseOrderLineExpanded}
               header={<OrderExpandHeader title="Closed" icon="check" isExpanded={isCloseOrderLineExpanded} />}
               onExpandToggle={() => setIsCloseOrderLineExpanded(!isCloseOrderLineExpanded)}
            >
               <OrderLinesTable
                  acceptWebp={acceptWebp}
                  orderLines={closedOrderLines}
                  locale={locale}
                  isConsumer={isConsumer}
                  orderBaseUrls={orderBaseUrls}
                  openedFrom={openedFrom}
                  showInvoiceDocuments={showInvoiceDocuments}
                  page={closedOrderLinesPage}
                  onPageChange={setClosedOrderLinesPage}
                  requestCoa={requestCoa}
                  requestBol={requestBol}
                  requestDeliveryNote={requestDeliveryNote}
                  downloadDocument={downloadDocument}
                  isUSBusinessContext={isUSBusinessContext}
               />
            </Expand>
         )}
      </Box>
   );
};
